import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './States.css';
import LargeDetailBox from '../../../components/common/DetailBox/LargeDetailBox';
import SmallDetailBox from '../../../components/common/DetailBox/SmallDetailBox';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';

const StateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchState = async () => {
      try {
        const response = await authenticatedAxios.get(`${baseurl}localization/state/?id=${id}`);
        if (response.data && response.data.data) {
          setState(response.data.data);
        } else {
          setErrors({ general: 'State not found.' });
        }
      } catch (error) {
        console.error('Error fetching state detail:', error);
        if (error?.response?.data?.error_code === 1001) {
          navigate('/permission-denied');
        } else {
          setErrors({ general: 'Failed to load state details.' });
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchState();
    }
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="whole-container">
        <p className="common-text fts-14 ftw-4" style={{ padding: '20px' }}>
          Loading state details...
        </p>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="whole-container">
        <div className="top-heading">
          <PageHeader
            pageName="Localization / States /"
            nestedpageName="Detail"
            linkTo="/states"
          />
        </div>
        <div className="page-container">
          <ErrorNotification errors={errors} marginBottom="16px" />
          <p className="common-text fts-14 ftw-4">State not found.</p>
        </div>
      </div>
    );
  }

  const flagUrl =
    state.country?.country_flag && typeof state.country.country_flag === 'string'
      ? `${baseurl.replace(/\/$/, '')}${state.country.country_flag.startsWith('/') ? '' : '/'}${state.country.country_flag}`
      : null;

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization / States /"
          nestedpageName="Detail"
          linkTo="/states"
        />
        <div className="export-container">
          <ExportLabel />
        </div>
      </div>

      <div className="page-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        {/* State Details Card */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">State Details</span>
            <button
              className="popup-add-button common-text white fts-15 ftw-5"
              onClick={() => navigate(`/states/edit/${state.id}`)}
            >
              Edit State
            </button>
          </div>

          <div className="card-body">
            <LargeDetailBox label="State Name" value={state.name || 'N/A'} />

            <div className="row-2">
              <SmallDetailBox label="State Code" value={state.state_code || 'N/A'} />
              <SmallDetailBox
                label="Status"
                value={state.is_archived ? 'Archived' : 'Active'}
              />
            </div>
          </div>
        </div>

        {/* Country Details Card */}
        <div className="features-card">
          <div className="card-header">
            <span className="card-title">Country Details</span>
          </div>

          <div className="card-body">
            {flagUrl && (
              <div>
                <label
                  className="detail-box-label fts-12 lt-gray"
                  style={{ display: 'block', marginBottom: '6px' }}
                >
                  Country Flag
                </label>
                <div className="country-detail-flag-wrapper">
                  <img
                    src={flagUrl}
                    alt={`${state.country?.name} flag`}
                    className="country-detail-flag-img"
                  />
                </div>
              </div>
            )}

            <LargeDetailBox
              label="Country Name"
              value={state.country?.name || 'N/A'}
            />

            {state.country?.id && (
              <div style={{ marginTop: '8px' }}>
                <span
                  className="country-pill clickable"
                  onClick={() => navigate(`/countries/detail/${state.country.id}`)}
                  title="Click to view country details"
                >
                  View Country Details &rarr;
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateDetail;
